/**
 * <volume-offer-component>
 *
 * Interaktiver Mengen-Staffelrabatt-Hinweis auf der Produktseite ("Kaufe X, spare Y").
 *
 * REINE DISPLAY-SCHICHT. Der tatsaechliche Rabatt wird von einem nativen Shopify
 * Automatic Discount am Checkout gewaehrt. Die Staffeln hier muessen mit der
 * Discount-Konfiguration im Admin uebereinstimmen (Quelle: Produkt-Metafield
 * `lora.volume_offer`).
 *
 * Lauscht auf die Theme-Events:
 *   - `quantity-selector:update` (detail.quantity, detail.cartLine) -> Live-Menge auf der PDP
 *   - `variant:update` (detail.resource.price in Cent)               -> Preis bei Variantenwechsel
 *   - `cart:update` (detail.resource.items)                          -> Cart-Menge dieses Produkts
 *
 * Die "effektive Menge" fuer die Staffel = Warenkorb-Menge (ueber ALLE Varianten/Farben
 * des Produkts) + aktuelle PDP-Auswahl. So fuellt sich der Balken auch, wenn der Rabatt
 * farbuebergreifend erreicht wird (z.B. 1x Gelb + 1x Orange im Warenkorb).
 *
 * Erwartet als data-Attribute:
 *   - data-tiers       JSON: [{ "min": 3, "percent": 20 }, ...]
 *   - data-unit-price  Stueckpreis der aktuellen Variante in Cent
 *   - data-product-id  Produkt-ID (Cart-Matching ueber line.product_id)
 *   - data-currency    ISO-Code, z.B. "EUR"
 *   - data-locale      z.B. "de-DE"
 *   - data-unit        Einheitswort Singular, z.B. "Stück", "Bund", "Stiel" (Fallback "Artikel")
 *   - data-unit-plural Einheitswort Plural, z.B. "Stück", "Bund", "Stiele" (Fallback = Singular)
 */
class VolumeOfferComponent extends HTMLElement {
  connectedCallback() {
    this.tiers = this.#parseTiers();
    if (this.tiers.length === 0) {
      this.hidden = true;
      return;
    }

    this.unitPrice = parseInt(this.dataset.unitPrice || '0', 10) || 0;
    this.currency = this.dataset.currency || 'EUR';
    this.locale = this.dataset.locale || 'de-DE';
    this.unit = this.dataset.unit || 'Artikel';
    this.unitPlural = this.dataset.unitPlural || this.unit;
    this.money = new Intl.NumberFormat(this.locale, {
      style: 'currency',
      currency: this.currency,
    });

    this.refs = {
      fill: this.querySelector('[data-vo-fill]'),
      track: this.querySelector('[data-vo-track]'),
      message: this.querySelector('[data-vo-message]'),
      price: this.querySelector('[data-vo-price]'),
      priceLabel: this.querySelector('.volume-offer__price-label'),
      priceOld: this.querySelector('[data-vo-price-old]'),
      priceNew: this.querySelector('[data-vo-price-new]'),
      chips: Array.from(this.querySelectorAll('[data-vo-chip]')),
    };

    this.productId = parseInt(this.dataset.productId || '0', 10) || 0;
    this.cartQty = 0;
    this.quantity = this.#initialQuantity();
    this.#ensurePriceNotes();

    document.addEventListener('quantity-selector:update', this.#handleQuantity);
    document.addEventListener('variant:update', this.#handleVariant);
    document.addEventListener('cart:update', this.#handleCart);

    this.#render();
    this.#fetchCartQty();
  }

  disconnectedCallback() {
    document.removeEventListener('quantity-selector:update', this.#handleQuantity);
    document.removeEventListener('variant:update', this.#handleVariant);
    document.removeEventListener('cart:update', this.#handleCart);
  }

  /** Cart-Page-Updates haben eine cartLine -> ignorieren, nur PDP-Selector zaehlt. */
  #handleQuantity = (event) => {
    if (event.detail?.cartLine != null) return;
    const q = parseInt(event.detail?.quantity, 10);
    if (!Number.isFinite(q) || q < 1) return;
    this.quantity = q;
    this.#render();
  };

  #handleVariant = (event) => {
    const price = event.detail?.resource?.price;
    if (typeof price === 'number' && Number.isFinite(price)) {
      this.unitPrice = price;
      this.#render();
    }
  };

  /** Cart-Update (Add/Change im Drawer o.ae.) -> Cart-Menge dieses Produkts neu zaehlen. */
  #handleCart = (event) => {
    const cart = event.detail?.resource;
    if (cart && Array.isArray(cart.items)) {
      this.#applyCartItems(cart.items);
    } else {
      this.#fetchCartQty();
    }
  };

  /** Initialer Cart-Stand beim Laden (es gibt noch kein cart:update-Event). */
  #fetchCartQty() {
    if (!this.productId) return;
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then((response) => (response.ok ? response.json() : null))
      .then((cart) => {
        if (cart && Array.isArray(cart.items)) this.#applyCartItems(cart.items);
      })
      .catch(() => {});
  }

  /** Summiert die Menge dieses Produkts ueber ALLE Varianten/Farben im Warenkorb. */
  #applyCartItems(items) {
    let qty = 0;
    for (const item of items) {
      if (item && Number(item.product_id) === this.productId) {
        qty += Number(item.quantity) || 0;
      }
    }
    this.cartQty = qty;
    this.#render();
  }

  /** Effektive Menge fuer die Staffel = bereits im Warenkorb + aktuelle PDP-Auswahl. */
  #effectiveQty() {
    return (this.cartQty || 0) + (this.quantity || 0);
  }

  #parseTiers() {
    let raw = [];
    try {
      raw = JSON.parse(this.dataset.tiers || '[]');
    } catch {
      raw = [];
    }
    if (!Array.isArray(raw)) return [];
    return raw
      .map((t) => ({ min: parseInt(t.min, 10), percent: parseFloat(t.percent) }))
      .filter((t) => Number.isFinite(t.min) && t.min > 0 && Number.isFinite(t.percent) && t.percent > 0)
      .sort((a, b) => a.min - b.min);
  }

  /** Sucht den Mengen-Input im selben Produkt-Formular fuer den Startwert. */
  #initialQuantity() {
    const scope = this.closest('.shopify-section') || document;
    const input = scope.querySelector('input[name="quantity"]');
    const value = input ? parseInt(input.value, 10) : 1;
    return Number.isFinite(value) && value > 0 ? value : 1;
  }

  /**
   * Haengt an jeden Hauptpreis-Block (`.pdp-active-price`, inkl. Sticky-Bar) eine
   * Notiz-Zeile fuer den rabattierten Einzelpreis an. Wird in #render gefuellt/versteckt.
   */
  #ensurePriceNotes() {
    this.priceNotes = [];
    document.querySelectorAll('.pdp-active-price').forEach((el) => {
      let note = el.querySelector('.pdp-active-price__volume-note');
      if (!note) {
        note = document.createElement('span');
        note.className = 'pdp-active-price__volume-note';
        note.hidden = true;
        note.style.cssText = 'display:block;margin-block-start:0.1rem;font-size:0.8125rem;font-weight:600;line-height:1.3;';
        const value = el.querySelector('.pdp-active-price__value');
        if (value) value.insertAdjacentElement('afterend', note);
        else el.appendChild(note);
      }
      this.priceNotes.push(note);
    });
  }

  #currentTier() {
    const q = this.#effectiveQty();
    let current = null;
    for (const tier of this.tiers) {
      if (q >= tier.min) current = tier;
    }
    return current;
  }

  #nextTier() {
    const q = this.#effectiveQty();
    return this.tiers.find((tier) => q < tier.min) || null;
  }

  #render() {
    const current = this.#currentTier();
    const next = this.#nextTier();
    const percent = current ? current.percent : 0;

    // Fortschritt Richtung naechstes Ziel (oder voll, wenn alle Stufen erreicht).
    const effective = this.#effectiveQty();
    const goal = next ? next.min : current ? current.min : this.tiers[0].min;
    const pct = Math.max(0, Math.min(100, Math.round((effective / goal) * 100)));
    if (this.refs.fill) this.refs.fill.style.inlineSize = `${pct}%`;
    if (this.refs.track) this.refs.track.setAttribute('aria-valuenow', String(pct));

    this.classList.toggle('volume-offer--active', percent > 0);

    // Aktive / erreichte Chips markieren.
    this.refs.chips.forEach((chip) => {
      const min = parseInt(chip.dataset.voChip, 10);
      chip.classList.toggle('volume-offer__chip--active', current != null && min === current.min);
      chip.classList.toggle('volume-offer__chip--reached', effective >= min);
    });

    if (this.refs.message) {
      this.refs.message.innerHTML = this.#messageHtml(current, next, effective);
    }

    // Projektions-Stückpreis: immer den relevanten Tier-Preis zeigen, live gekoppelt
    // an Hauptpreis (this.unitPrice) UND Menge (this.quantity). Ist eine Stufe erreicht,
    // zeigt es den aktiven Rabattpreis, sonst eine Vorschau auf die nächste Stufe.
    if (this.refs.price) {
      const relevant = current || this.tiers[0];
      if (relevant && this.unitPrice > 0) {
        const discounted = Math.round(this.unitPrice * (1 - relevant.percent / 100));
        if (this.refs.priceOld) this.refs.priceOld.textContent = this.money.format(this.unitPrice / 100);
        if (this.refs.priceNew) this.refs.priceNew.textContent = this.money.format(discounted / 100);
        if (this.refs.priceLabel) {
          this.refs.priceLabel.textContent = current
            ? `Preis pro ${this.unit} mit Rabatt:`
            : `Preis pro ${this.unit} ab ${relevant.min}:`;
        }
        this.refs.price.hidden = false;
      } else {
        this.refs.price.hidden = true;
      }
    }

    // Rabattierten Einzelpreis am Hauptpreis (€-Block) zeigen, sobald eine Stufe
    // ERREICHT ist. Abgeleitet aus dem aktuellen Hauptpreis (this.unitPrice) + Menge.
    if (this.priceNotes && this.priceNotes.length) {
      const active = current && this.unitPrice > 0;
      const each = active ? Math.round(this.unitPrice * (1 - current.percent / 100)) : 0;
      this.priceNotes.forEach((note) => {
        if (active) {
          note.textContent = `Mit Mengenvorteil: ${this.money.format(each / 100)} / ${this.unit}`;
          note.hidden = false;
        } else {
          note.hidden = true;
        }
      });
    }
  }

  #messageHtml(current, next, effective) {
    if (next) {
      const remaining = Math.max(1, next.min - effective);
      const goal = `<strong>${next.percent}% Rabatt</strong>`;
      const noun = remaining === 1 ? this.unit : this.unitPlural;
      const count = `<strong>${remaining} ${noun}</strong>`;
      if (current) {
        const lead = this.cartQty >= current.min ? `${current.percent}% aktiv` : `${current.percent}% ab dieser Menge`;
        return `${lead} · noch ${count} bis ${goal}`;
      }
      return `Noch ${count} bis ${goal}`;
    }
    // Oberste Stufe erreicht: aktiv (schon im Warenkorb) oder per aktueller Auswahl.
    if (!current) return '';
    if (this.cartQty >= current.min) {
      return `<strong>${current.percent}% Rabatt</strong> aktiv &#127881;`;
    }
    return `<strong>${current.percent}% Rabatt</strong> mit dieser Auswahl`;
  }
}

if (!customElements.get('volume-offer-component')) {
  customElements.define('volume-offer-component', VolumeOfferComponent);
}
